import { APPOINTMENT_NOTE_TYPES } from "./AppointmentNote";
import { AppointmentStatus, STATUS_IDENTIFIERS } from "./AppointmentStatus";
import { BrandGroup, BrandGroupID } from "./Brand";
import { Checklist, ChecklistID } from "./Checklist";
import { CommunicationSetting } from "./CommunicationSetting";
import { CommunicationMedium } from "./CustomerCommunication";
import { Dealer, DealerID } from "./Dealer";
import { DealerDMSID, DealerLocationDMS, DMS_CAPABILITIES, DMSIDS } from "./Dms";
import { ExactFailedInvoice } from "./ExactFailedInvoice";
import { KeyLockerPIN } from "./Keylocker";
import { LANGUAGE_CODES } from "./Language";
import { NoteSuggestion, NoteSuggestionType } from "./NoteSuggestion";
import { AAEquipmentTyreScanner } from "./TyreScanner";

export type DealerLocationID = number;

export type PlanItPlanningEstablishmentID = number;

export type WebClockOmgevingID = number;

export type NotifierKey = string;

export type TimeslotID = number;

export type LocationReportTemplateID = number;

export type DealerLocationIPAddressID = number;

export enum BARCODE_TYPES {
  Barcode128 = 1,
  Barcode93 = 2,
  Barcode39 = 3,
  Barcode25 = 4,
  Barcode11 = 5
}

export enum KEYLABEL_TYPES {
  Standard = 1,
  Basic = 2
}

export enum KEYLABEL_LAYOUTS {
  A4 = 1,
  Label = 2,
  KioskLabel = 3,
  KeyTag = 4,
  AltecQR = 5,
  LongLabel = 6,
  LabelWriter400 = 7
}

export interface DealerLocation {
  created_on: string;
  updated_on: string;
  id: DealerLocationID; // Claire dealer location ID
  external_location_id: string; // External location ID, used by third parties to match Claire location ids with the third parties location ids
  notifier_key: NotifierKey; // Authentication key used for the notifier websockets
  dms_id: DMSIDS; // DMS ID in Claire
  dsn: string; // Set if a different DSN per location is necessary at the DMS side, otherwise empty and the dealer DSN is used
  dms_write_back: boolean; // Is write back to DMS enabled
  upgrade_car_with_rdw: boolean; // Is updating the car with information from rdw enabled
  upgrade_with_rdw_apk_date: boolean; // Upgrades the APK Date of the car with what is from RDW
  write_back_rdw_apk_date: boolean; // Writeback the APK Date to the DMS if the RDW APK Date is more recent
  dms_location_source: string; // DMS Datasource for the location
  keep_importing_after_work_started: boolean; // Interventions will be imported even after work has started on the appointment
  active: boolean; // Location is active if true
  track_dms_importing: boolean; // If data is missing, a support ticket will be created
  name: string; // Name
  commercial_name: string; // Commercial name of the Claire Dealer Location
  headline: string; // Headline
  footerline: string; // Footerline
  logo: string; // Logo URL
  company_stamp: string; // Official stamp of approval given by the location
  language_code: LANGUAGE_CODES; // Default language for the location
  vat: number; // VAT
  include_vat: boolean; // Include VAT
  importer_version: string; // Version of the importer
  summer_a_threshold: number; // A Threshold for summer tyres
  summer_n_threshold: number; // N Threshold for summer tyres
  winter_a_threshold: number; // A Threshold for winter tyres
  winter_n_threshold: number; // N Threshold for winter tyres
  all_season_a_threshold: number; // A Threshold for all_season tyres
  all_season_n_threshold: number; // N Threshold for all_season tyres
  truck_tyre_a_threshold: number; // A Threshold for truck tyres
  truck_tyre_n_threshold: number; // N Threshold for truck tyres
  car_ready_btn_visible: boolean; // Car ready button is visible
  apk_visible: boolean; // APK is visible
  hu_visible: boolean; // HU is visible
  date_visible: boolean; // Date is visible
  accept_network_metrics: boolean; // Accept network metrics
  last_import_at: string | null; // Last imported at time stamp
  street: string; // Street
  postal_code: string; // Postal code
  city: string; // City
  country: string; // Country
  phone_nr: string; // Phone number
  dealer_id: DealerID; // Dealer Identifier
  extra_parts_list_id: ChecklistID | null; // Extra parts list ID
  default_customer: string; // Default customer
  dp_driver_visible: boolean; // DP Driver is visible
  dp_contractor_visible: boolean; // DP Contractor is visible
  dp_owner_visible: boolean; // DP Owner is visible
  unpin_appointments_on_is_billed: boolean; // Unpin appointments automatically when they are considered billed
  unpin_appointments_on_car_out_of_shop: boolean; // Unpin appointments automatically when the Car leaves the location
  unpin_past_appointments_older_than: number; // Unpin appointments automatically once their date is this many days in the past
  unpin_future_appointments_further_than: number; // Unpin appointments automatically once their date is this many days in the future
  pin_visible: boolean; // Pin is visible
  video_enabled: boolean; // Video is enabled
  schedule_enabled: boolean; // Schedule is enabled
  first_name_optional: boolean; // First name is optional
  mcc_button_visible: boolean; // MCC button is visible
  is_robnet_enabled: boolean; // Robnet is enabled
  robnet_location_number: number | null; // Robnet location number, used to build the ROB supplier portal URL of a service request
  customer_communication_visible: boolean; // Customer Communication is visible
  datastore_key: string; // Datastore key
  fleet_nr_visible: boolean | null; // Fleet number is visible
  service_box_visible_on_car: boolean; // Service box visible on car
  service_box_visible_on_wo: boolean; // Service box visible on wo
  dbb_enabled: boolean; // DBB is enabled
  dbb_location_id: string; // DBB location identifier
  dbb_user: string; // DBB user
  ip_address_enabled: boolean; // IP Address is enabled
  dms_price_enabled: boolean; // DMS Prices is enabled
  dms_billing_enabled: boolean; // DMS Billing is enabled
  dms_warranty_pin: boolean; // DMS Warranty Pin is enabled
  third_party_send_communications: boolean; // Third party can be used for sending communications
  online_check_in_enabled: boolean; // Online check in is enabled
  desk_check_in_enabled: boolean; // Desk check in is enabled
  is_open_desk_checkout_on_car_out_of_shop_enabled: boolean; // Desk Checkout will automatically open when the receptionist sets Car out of Shop
  car_out_of_shop_at_desk_checkout_enabled: boolean; // At Desk Checkout, car will be set out of shop automatically
  diagnose_overview_enabled: boolean; // Diagnose overview is enabled
  repair_overview_enabled: boolean; // Repair overview is enabled
  default_customer_ok_for_dms_lease_intervention: boolean; // Default customer ok for DMS Intervention with lease customer
  is_dms_event_enabled: boolean; // Use DMS events instead of date range polling
  last_webhook_received_at: string; // Timestamp of the last received webhook from keyloop
  webhooks_per_hour_last_reset_at: string; // Timestamp of the last reset of webhooks per hour
  previous_webhooks_per_hour: number; // Previous rate of received webhooks per hour
  webhooks_per_hour: number; // Current rate of received webhooks per hour
  webhook_low_threshold_per_minute: number; // Threshold value to conclude if the webhook count per minute is low or high
  autoflex_username: string; // Autoflex username
  appointment_detail_users_indicator_enabled: boolean; // Appointment detail users indicator enabled
  is_employee_name_visible: boolean; // Employee name is visible
  is_car_in_shop_print_enabled: boolean; // Car in shop print is enabled
  is_keylocker_enabled: boolean; // KeyLocker is enabled
  is_tablet_translation_ai_enabled: boolean; // Indicates if a location can use AI translations on the tablet
  keylocker_code: KeyLockerPIN; // KeyLocker Code
  is_tyre_team_enabled: boolean; // Tyreteam is enabled
  is_tyre_team_autofill_price_ref_on_order: boolean; // When ordering tyres, a ref field will be autofilled with the price
  is_tyre_net_enabled: boolean; // TyreNet is enabled and the location can retrieve tyres from it
  is_tire_scanner_enabled: boolean; // Tyre scanner is enabled
  tire_scanner_private_key: string; // Tire scanner private key
  is_aaequipment_enabled: boolean; // AAEquipment tire scanner is enabled
  is_editing_question_videos_enabled: boolean; // Question video editing is enabled
  automatically_pin_appointments: boolean; // Automatically pinning appointments is enabled
  is_manually_pinning_appointments_enabled: boolean; // Manually pinning appointments is enabled
  automatically_pin_car_in_shop: boolean; // if enabled, appointment will be pinned on setting car in shop, and it will be unpinned automatically if no other condition will force it to be in pinned state.
  is_automatically_pinned_appointments_readonly: boolean; // Automatically pinned appointments are readonly is enabled
  is_dayplanner_enabled: boolean; // Dayplanner is enabled
  automatically_assign_wo_when_car_check_started: boolean; // Automatically assign the workorder to the mechanic who started the check
  automatically_assign_wo_when_intervention_fixed: boolean; // Automatically assign the workorder to the mechanic who fixed the intervention
  recurring_car_duration: number; // Duration in week to mark a car as recurring car, default is 4 weeks
  automatic_appointment_refresh_interval: number; // When the location is connected to a Scheduler DMS. This will automatically update appointments, upon opening, if the elapsed time surpasses the specified interval
  video_capture_length_in_seconds: number; // Used in tablet apps to define the maximum duration of the video capture
  planit_planning_establishment_id: PlanItPlanningEstablishmentID | null; // PlanIt Planning Identifier for the location
  planit_planning_replacement_vehicle: boolean; // Convert the planit replacement vehicle into a note on the appointment
  convert_checklist_to_intervention_is_enabled: boolean; // Set it to true to enable converting a checklist to an intervention
  manually_create_intervention_is_enabled: boolean; // Set it to true to enable manually creating interventions
  create_wo_is_enabled: boolean; // Set it to true to enable manually creating workorders
  last_manually_created_wo: string | null; // Last time an appointment was created manually in claire. Used to reset the counter daily
  manually_created_wo_counter: number; // Counter to generate WONrs for manually created appointments
  can_edit_checklist_after_car_ready: boolean; // Set it to true to enable editing the checklist after the car is ready
  can_add_intervention_elements: boolean; // Set it to true to enable adding elements to interventions, by default it is enabled
  is_assigned_wo_status_change_notification_enabled: boolean; // Set it to true to enable sending notifications when the wo is assigned without status changed.
  is_three_departments_enabled: boolean; // Set it to true to enable the three departments feature
  is_tyre_status_always_visible_on_wo: boolean; // Set it to true to enable always showing the tyre status on the work order
  web_clock_omgeving_id: WebClockOmgevingID | null; // ID from the Omgeving in TruckVision database to use WebClock
  bar_code_type: BARCODE_TYPES; // Type of barcode to be scanned
  is_warranty_pin_support_warning_enabled: boolean; // Is support nr warning enabled on the warranty pin
  is_warranty_pin_claim_warning_enabled: boolean; // Is claim nr warning enabled on the warranty pin
  is_recall_pin_support_warning_enabled: boolean; // Is support nr warning enabled on the recall pin
  is_recall_pin_claim_warning_enabled: boolean; // Is claim nr warning enabled on the recall pin
  is_warranty_pin_signature_missing_warning_enabled: boolean; // Is warning enabled for missing signature on warranty pin
  evhc_export_enabled: boolean; // EVHC exports are Enabled
  evhc_primary_code: string; // EVHC Primary Dealer Code
  evhc_secondary_code: string; // EVHC Secondary Dealer Code
  is_customer_answered_checkin_mandatory: boolean; // Is customer answered checkin mandatory
  is_next_km_and_next_date_optional: boolean; // Next Km and Next Date are optional fields
  hide_next_km_and_next_date: boolean; // Hide next km and next date on the wo detail page
  is_sharebox_enabled: boolean; // Sharebox credentials are enabled
  sharebox_api_client_id: string; // Sharebox API Client ID used as client_id during authentication
  sharebox_customer_id: string; // Sharebox customer id
  sharebox_session_id: string; // Sharebox session id
  is_kiosk_enabled: boolean; // Kiosks are enabled
  is_acses_enabled: boolean; // Acses lockers are enabled
  acses_email: string; // Acses email used as Basic Auth during authentication
  is_recalls_service_enabled: boolean; // Recalls service is enabled
  is_pon_enabled: boolean; // PON API is enabled
  pon_location_number: string; // PON Location Number
  xentry_sub_outlet_id: string; // Location Identifier for XEntry
  exact_account_id?: string; // ID of the linked account in Exact Online
  is_exact_invoicing_enabled: boolean; // Use Exact invoice generation
  exact_is_category_licence_enabled: boolean; // Use Exact category licenses
  is_exact_location_startup_costs_invoiced: boolean; // Is the startup cost for this location already invoiced
  is_tyre_motion_enabled: boolean; // Is the usage of tyre motion api enabled on this location
  key_label_type_id: KEYLABEL_TYPES; // The Key Label type is used to include standard/basic data in the key label
  key_label_layout_id: KEYLABEL_LAYOUTS; // This key label layout represents the default layout for the key label at a location
  redirect_to_new_web_version: boolean; // Redirect the user to new web version
  is_lef_enabled: boolean; // Is the usage of LEF api enabled on this location
  lef_account_number: string; // LEF Account Number
  generate_ai_summary: boolean; // Enable it to generate summary in customer communication using AI.
  is_car_note_enabled: boolean; // Car notes can be created and updated on this location
  is_customer_note_enabled: boolean; // Customer notes can be created and updated on this location
  check_in_days_prior: number;
  send_parking_gate_code?: boolean;
  cannot_decline_warranty_types?: boolean;
  brand_groups?: BrandGroup[]; // Brand Groups
  warranty_pin_signature_brand_groups?: BrandGroup[]; // Brand groups requiring a warranty pin signature
  checklists?: Checklist[];
  statuses?: AppointmentStatus[];
  timeslots?: Timeslot[];
  dealer?: Dealer;
  dealer_name?: string;
  dms_capability_ids?: DMS_CAPABILITIES[];
  last_dms_update?: string;
  auto_unpin_appointment_statuses?: STATUS_IDENTIFIERS[];
  has_autoflex_password?: boolean; // Autoflex password has been set
  has_sharebox_api_client_secret?: boolean; // Sharebox API Client secret used as client_secret during authentication has been set
  has_sharebox_weblink_access_key?: boolean; // Sharebox weblink access key has been set
  has_sharebox_private_key?: boolean; // Sharebox private key has been set
  has_dbb_password?: boolean; // DBB Password has been set
  has_robnet_api_key?: boolean; // Robnet API Key has been set
  has_acses_password?: boolean; // Acses password has been set
  has_tyre_net_tic_uid: boolean | null; // TyreNet Tic UID has been set
  has_tyre_net_tic_id: boolean | null; // TyreNet Tic ID has been set
  has_failed_invoices: boolean | null; // Has failed Exact invoices
  exact_failed_invoices?: ExactFailedInvoice[]; // Exact failed Exact invoices of this location
  invoice_error: string | null; // Last error message for Exact invoicing
  last_invoice_time: string | null; // Last invoice time for Exact invoices
  note_suggestions?: NoteSuggestion[]; // List of suggestions for notes
  note_suggestion_types?: NoteSuggestionType[]; // List of types of notes for the suggestions
  dealer_location_dms?: DealerLocationDMS[]; // List of dms configurations for this location
  brand_group_ids?: BrandGroupID[]; // Brand group IDs list
  number_of_missing_exact_licenses?: number; // The quantity of Exact license categories that don't have a connected license'
  enabled_appointment_note_type_ids?: APPOINTMENT_NOTE_TYPES[]; // List of enabled Appointment Note Types
  aa_equipment_tyre_scanners?: AAEquipmentTyreScanner[]; // List of AAEquipment tyre scanner linked to a short code
  ip_addresses?: string[]; // List of allowed IP addresses for the location
  dealer_dms_id?: DealerDMSID; // Dealer DMS Identifier
  is_bird_whats_app_webhook_set?: boolean; // Webhook is configured for customer communication via Bird WhatsApp
  star_rating_experience_message?: string; // Star rating experience message
  star_rating_service_message?: string; // Star rating service message
  star_rating_speed_message?: string; // Star rating speed message
  hide_warranty_intervention_price?: boolean; // Hide price for warranty type intervention
  hide_recall_intervention_price?: boolean; // Hide price for recall type intervention
  communication_settings?: CommunicationSetting; // Communication setting of the location
  communication_mediums?: CommunicationMedium[]; // Communication Mediums of the location
  claire_version?: string; // Claire Version
  is_service_tracker_enabled?: boolean; // True if service tracker is enabled for this location
}

export interface LocationAttachDetach {
  checklist_id: ChecklistID;
  dealer_location_id: DealerLocationID;
  dealer_id: DealerID;
  display_order: number;
}

export interface Timeslot {
  created_on: string;
  updated_on: string;
  id: TimeslotID;
  deadline: string;
  active: boolean;
  dealer_location_id: DealerLocationID;
}

export interface LocationReportTemplate {
  created_on: string;
  id: LocationReportTemplateID;
  filename: string;
  name: string;
  report_type_id: number;
  unit: string;
  thresholds: string;
  report_query_atributes: string;
  ordering: number;
  template_group: number;
  CC: number;
  report_type?: string;
}

export interface ReportData {
  id: LocationReportTemplateID; // Identifier for the location report template
  report_type: string; // Type of the report
  unit: string; // Units to represent the data elements
  thresholds: string; // Threshold values
  report_name: string; // Name of the report
  ordering: number; // Order of the report
  report_data?: string[];
  report_colors?: ReportColor[];
}

export interface ReportColor {
  name: string;
  color: string;
}

export interface AutoUnpinAppointmentStatuses {
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  appointment_status_identifier: STATUS_IDENTIFIERS; // Appointment Status Identifier
}

export interface AppointmentNoteTypeDealerLocations {
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  appointment_note_type: APPOINTMENT_NOTE_TYPES; // Appointment Note Type
}

export interface DealerLocationIPAddress {
  created_on: string;
  id: DealerLocationIPAddressID; // Claire dealer location IP address ID
  ip_address: string; // Allowed IP address for the location
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
}

export interface DealerLocationBrandGroups {
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  brand_group_id: BrandGroupID; // Brand Group Identifier
}

export interface WarrantyPinSignatureBrandGroups {
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  brand_group_id: BrandGroupID; // Brand Group Identifier
}
